import { FC, useMemo } from 'react';
import { Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import type { BuyingPointSummaryRow } from '../threeMonth.types';
import { formatCurrency, formatPercent } from '../utils/forecastingAggregations';
import { ChartTitle, DrillToolbar, TablePanel } from '../Forecasting.styles';
import { FORECASTING_TABLE_CSS } from '../forecastingTableStyles';
import { DrillHeader } from './DrillHeader';

interface BuyingPointSummaryTableProps {
  data: BuyingPointSummaryRow[];
  levelTitle: string;
  canDrillUp: boolean;
  canDrillDown: boolean;
  onDrillUp: () => void;
  onDrillDown: () => void;
  selectedKey: string | null;
  onRowSelect: (row: BuyingPointSummaryRow) => void;
}

export const BuyingPointSummaryTable: FC<BuyingPointSummaryTableProps> = ({
  data,
  levelTitle,
  canDrillUp,
  canDrillDown,
  onDrillUp,
  onDrillDown,
  selectedKey,
  onRowSelect
}) => {
  const columns: ColumnsType<BuyingPointSummaryRow> = useMemo(
    () => [
      {
        title: (
          <DrillHeader
            title={levelTitle}
            canUp={canDrillUp}
            canDown={canDrillDown}
            onDrillUp={onDrillUp}
            onDrillDown={onDrillDown}
          />
        ),
        dataIndex: 'label',
        key: 'label',
        ellipsis: true,
        width: 120
      },
      {
        title: 'Booked',
        dataIndex: 'booked',
        key: 'booked',
        align: 'right',
        render: (v: number) => formatCurrency(v)
      },
      {
        title: 'Pipe',
        dataIndex: 'pipe',
        key: 'pipe',
        align: 'right',
        render: (v: number) => formatCurrency(v)
      },
      {
        title: 'B + P',
        dataIndex: 'bookedPlusPipe',
        key: 'bookedPlusPipe',
        align: 'right',
        render: (v: number) => formatCurrency(v)
      },
      {
        title: 'Dynamic Forecast',
        dataIndex: 'dynamicForecast',
        key: 'dynamicForecast',
        align: 'right',
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.dynamicForecast - b.dynamicForecast,
        render: (v: number) => formatCurrency(v)
      },
      {
        title: 'Target',
        dataIndex: 'target',
        key: 'target',
        align: 'right',
        render: (v: number) => formatCurrency(v)
      },
      {
        title: 'DF vs Target Rev',
        dataIndex: 'vsTargetRevPct',
        key: 'vsTargetRevPct',
        align: 'right',
        width: 116,
        render: (v: number | null) => (v != null ? formatPercent(v, 0) : '—')
      },
      {
        title: 'DF vs Target GP',
        dataIndex: 'vsTargetGpPct',
        key: 'vsTargetGpPct',
        align: 'right',
        width: 116,
        render: (v: number | null) => (v != null ? formatPercent(v, 0) : '—')
      }
    ],
    [levelTitle, canDrillUp, canDrillDown, onDrillUp, onDrillDown]
  );

  const totals = data.reduce(
    (acc, row) => ({
      booked: acc.booked + row.booked,
      pipe: acc.pipe + row.pipe,
      bookedPlusPipe: acc.bookedPlusPipe + row.bookedPlusPipe,
      dynamicForecast: acc.dynamicForecast + row.dynamicForecast,
      target: acc.target + row.target
    }),
    { booked: 0, pipe: 0, bookedPlusPipe: 0, dynamicForecast: 0, target: 0 }
  );

  return (
    <TablePanel>
      <DrillToolbar>
        <ChartTitle style={{ margin: 0 }}>{levelTitle} summary</ChartTitle>
        <div className="drill-actions">
          <span className="drill-hint">Drill level</span>
          <Tooltip title="Drill up">
            <button
              type="button"
              className="drill-btn"
              disabled={!canDrillUp}
              onClick={onDrillUp}
              aria-label="Drill up"
            >
              <CaretUpOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Drill down">
            <button
              type="button"
              className="drill-btn"
              disabled={!canDrillDown}
              onClick={onDrillDown}
              aria-label="Drill down"
            >
              <CaretDownOutlined />
            </button>
          </Tooltip>
        </div>
      </DrillToolbar>
      <Table<BuyingPointSummaryRow>
        size="small"
        pagination={false}
        rowKey="key"
        columns={columns}
        dataSource={data}
        scroll={{ x: 900, y: 300 }}
        className="forecasting-bp-table"
        onRow={(record) => ({
          onClick: () => onRowSelect(record),
          style: { cursor: 'pointer' }
        })}
        rowClassName={(record) => (record.key === selectedKey ? 'forecasting-row-selected' : '')}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>Grand total</Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                {formatCurrency(totals.booked)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                {formatCurrency(totals.pipe)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                {formatCurrency(totals.bookedPlusPipe)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">
                {formatCurrency(totals.dynamicForecast)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                {formatCurrency(totals.target)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                {totals.target > 0 ? formatPercent((totals.dynamicForecast / totals.target) * 100, 0) : '—'}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right">—</Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      <style>{FORECASTING_TABLE_CSS}</style>
    </TablePanel>
  );
};
